import React, { useMemo, useState } from "react";
import { updateListingPrice } from "../../../shared/api/resaleListings";
import { formatCents } from "../../../shared/formattingUtils";
import { MINIMUM_LIST_PRICE_CENTS } from "../lib/listingConstants";

const EditPriceDialog = ({ listing, onClose, onSaved }) => {
  const initial = listing.pricing_summary?.list_price_cents ?? listing.list_price_cents ?? 0;
  const [dollars, setDollars] = useState((initial / 100).toFixed(2));
  const [submitting, setSubmitting] = useState(false);

  const capCents = useMemo(() => {
    const cap = Number(listing.resale_price_cap_cents);
    return Number.isFinite(cap) && cap > 0 ? cap : null;
  }, [listing.resale_price_cap_cents]);

  const cents = useMemo(() => {
    if (dollars === "") return null;
    const n = Number(dollars);
    return Number.isNaN(n) ? null : Math.round(n * 100);
  }, [dollars]);

  const belowFloor = cents !== null && cents < MINIMUM_LIST_PRICE_CENTS;
  const aboveCap = capCents !== null && cents !== null && cents > capCents;
  const invalid = belowFloor || aboveCap;
  const canSave = cents !== null && !invalid && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSubmitting(true);
    try {
      const updated = await updateListingPrice({ id: listing.id, listPriceCents: cents });
      if (updated && updated.id) onSaved(updated);
      onClose();
    } catch (_e) {
      return;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="resale-modal-backdrop" role="dialog" aria-modal="true">
      <div className="resale-modal">
        <h3 className="resale-modal-title">Edit listing price</h3>
        <form onSubmit={handleSubmit}>
          <label className="resale-modal-label">
            Price per ticket
            <div className="resale-modal-input-wrap">
              <span className="resale-modal-input-prefix">$</span>
              <input
                type="number"
                required
                inputMode="decimal"
                step="0.01"
                min={(MINIMUM_LIST_PRICE_CENTS / 100).toFixed(2)}
                max={capCents !== null ? (capCents / 100).toFixed(2) : undefined}
                value={dollars}
                onChange={(e) => setDollars(e.target.value)}
                className="resale-modal-input"
                aria-invalid={invalid}
                aria-describedby={invalid ? "edit-price-error" : undefined}
                autoFocus
              />
            </div>
          </label>
          <div role="alert" aria-live="polite">
            {belowFloor && (
              <small id="edit-price-error" className="resale-form-help text-danger">
                Minimum listing price is {formatCents(MINIMUM_LIST_PRICE_CENTS)}.
              </small>
            )}
            {aboveCap && !belowFloor && (
              <small id="edit-price-error" className="resale-form-help text-danger">
                Maximum listing price is {formatCents(capCents)}.
              </small>
            )}
          </div>
          <div className="resale-modal-actions">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={!canSave}>
              {submitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPriceDialog;
